import React from 'react'
import { Phone, Mail } from 'lucide-react'
import Button from 'components/commercetools-ui/atoms/button'
import Image from 'components/commercetools-ui/atoms/image'
import Link from 'components/commercetools-ui/atoms/link'
import useClassNames from 'helpers/hooks/useClassNames'
import Container from '../Container'
import HeadingWrapper from '../HeadingWrapper'
import { useElementStyle } from '../helpers/useElementStyle'
import { isModuleActive } from '../helpers/useVisibilityConstrain'

const ContentstackContacts = ({
  headline,
  subline,
  contacts_content: content,
  content_below_columns,
  desktop_columns,
  tablet_columns,
  mobile_columns,
  layout_settings,
  visibility_settings,
}: ContentstackContacts) => {
  if (!content || !isModuleActive(visibility_settings)) return null

  const PLACEHOLDER_IMAGE = '/images/avatar_placeholder.png'

  const {
    getTextAlignmentClassName,
    getTextColorClass,
    getColumnClassName,
    getTabletColumnClassName,
    getDesktopColumnClassName,
    getButtonVariant,
  } = useElementStyle()

  const columnClassNames = `grid gap-y-48 ${getColumnClassName(mobile_columns || 2)} ${getTabletColumnClassName(tablet_columns || 2)} ${getDesktopColumnClassName(desktop_columns || 3)}`

  return (
    <Container layoutSettings={layout_settings}
      className='flex flex-col'
    >

      {headline && (
        <HeadingWrapper
          layoutSettings={layout_settings}
          className='mb-24'
        >{headline}
        </HeadingWrapper>
      )}

      {subline && (
        <div className={useClassNames([
          'mb-32',
          getTextAlignmentClassName(layout_settings.headline_alignment),
          getTextColorClass(layout_settings.headline_color),
        ])} dangerouslySetInnerHTML={{ __html: subline || '' }} />
      )}

      {content.length && (
        <div className={useClassNames([
          'mt-32',
          columnClassNames,
        ])}>
          {content.map((column, index) => {
            // Calculate if divider should be shown for each breakpoint
            const showMobileDivider = (mobile_columns || 2) > 1 && (index % (mobile_columns || 2)) !== 0
            const showTabletDivider = (tablet_columns || 3) > 1 && (index % (tablet_columns || 3)) !== 0
            const showDesktopDivider = (desktop_columns || 4) > 1 && (index % (desktop_columns || 4)) !== 0

            return (
              <div key={index} className={useClassNames([
                // Mobile divider
                showMobileDivider ? 'border-l border-gray-300 pl-32' : '',
                // Tablet divider (override mobile if needed)
                showTabletDivider ? 'md:border-l md:border-gray-300 md:pl-32' : 'md:border-l-0 md:pl-0',
                // Desktop divider (override tablet if needed)  
                showDesktopDivider ? 'lg:border-l lg:border-gray-300 lg:pl-32' : 'lg:border-l-0 lg:pl-0',
              ])}>
                <div className={useClassNames([
                  'font-heading text-4xl mb-32',
                  getTextColorClass(layout_settings.headline_color),
                ])}>{column.contact_column.column_headline}</div>
                <div className='flex flex-col gap-32'>
                  {column.contact_column.contacts && column.contact_column.contacts.map((contact, _index) => (
                    <div className='flex gap-16' key={_index}>

                      <div className='aspect-1 size-135 shrink-0 overflow-hidden rounded-full'>
                        <Image
                          src={contact?.image?.url || PLACEHOLDER_IMAGE}
                          width={135}
                          height={135}
                          style={{ objectFit: 'cover' }}
                        />
                      </div>

                      <div className='flex flex-col gap-[2px]'>
                        {contact.title && (
                          <div className={useClassNames([
                            'font-bold text-lg',
                            getTextColorClass(layout_settings.headline_color),
                          ])}>
                            {contact.title}
                          </div>
                        )}

                        {contact.job && (
                          <div className={useClassNames([
                            '',
                            getTextColorClass(layout_settings.headline_color),
                          ])}>
                            {contact.job}
                          </div>
                        )}

                        {contact.mail && (
                          <a href={`mailto:${contact.mail}`} className={useClassNames([
                            'flex items-center gap-8',
                            'hover:underline',
                            getTextColorClass(layout_settings.headline_color),
                          ])}>
                            <Mail size={16} />
                            {contact.mail}
                          </a>
                        )}

                        {contact.fon && (
                          <a href={`tel:${contact.fon}`} className={useClassNames([
                            'flex items-center gap-8',
                            'hover:underline',
                            getTextColorClass(layout_settings.headline_color),
                          ])}>
                            <Phone size={16} />
                            {contact.fon}
                          </a>
                        )}

                        {contact.description && (
                          <div className={useClassNames([
                            'my-6 italic',
                            getTextColorClass(layout_settings.headline_color),
                          ])} dangerouslySetInnerHTML={{ __html: contact.description || '' }} />
                        )}

                        {contact.citation && (
                          <div className={useClassNames([
                            'mt-6 font-bold',
                            getTextColorClass(layout_settings.headline_color),
                          ])}>"{contact.citation}"</div>
                        )}

                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )
          })}
        </div>
      )}
      {(content_below_columns?.teaser_text || (content_below_columns?.addition_links && content_below_columns?.addition_links.length)) && (
        <div>
          {content_below_columns?.teaser_text && (
            <div className={useClassNames([
              'mt-48',
              getTextColorClass(layout_settings.headline_color),
            ])} dangerouslySetInnerHTML={{ __html: content_below_columns.teaser_text || '' }} />
          )}

          {content_below_columns?.addition_links && content_below_columns?.addition_links.length && (
            <div className='mt-32 flex gap-32'>
              {content_below_columns.addition_links.map((linkItem, index) => (
                <Link link={linkItem.href || ''} title={linkItem?.title || ''} key={index}>
                  <Button variant={getButtonVariant(content_below_columns.button_style || 'primary')} size='m' className='w-full'>
                    {linkItem?.title || ''}
                  </Button>
                </Link>
              ))}
            </div>
          )}
        </div>
      )}
    </Container >
  )
}

export default ContentstackContacts
